import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollToTop, useScrollToTopOnChange } from '../utils/scrollUtils';
import {
  X,
  MapPin,
  Clock,
  Car,
  ShoppingBag,
  Utensils,
  Fuel,
  Eye,
  Wrench,
  Heart,
  Share2,
  Layout,
  Camera,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Package,
  ShoppingCart,
  Apple,
  Beef,
  ChefHat,
  Droplets,
  Home,
  Sparkles,
  Shirt,
  Trees,
  Waves,
  Wine,
  Pill,
  Stethoscope,
  Dumbbell,
  UtensilsCrossed,
  Coffee,
  Milk,
  IceCream,
  Fish,
  Lightbulb,
  Fan,
  Microwave,
  Refrigerator,
  Laptop,
  Tv,
  Headphones,
  Gem,
  Gamepad2,
  Box,
  LogIn,
  LogOut,
  CreditCard,
  Leaf,
  Croissant,
  Smartphone,
  Store,
  RotateCcw as ReturnIcon,
  Calendar,
  Info,
  Snowflake,
  Tag,
  Navigation,
} from 'lucide-react';
import { CostcoLocation } from '../types';
import { getLocalCurrentDateTime, useDateHandler } from '../utils/dates';
import { BusynessTab, ParkingTab, TrafficTab, CrowdLevelChart } from './LocationModal';
// import FeedbackBubble from './FeedbackBubble'; // Hidden for now
import './Modal.css';
import './CostcoModal.css';
import { eventLogger } from '../utils/auth';

interface CostcoModalProps {
  location: CostcoLocation;
  handleClose: () => void;
}

const CostcoModal: React.FC<CostcoModalProps> = ({ location, handleClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const headerRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    eventLogger('location_tab', { location: location.name, tab: activeTab });
  }, [activeTab]);

  // Measure header and tabs height for content positioning
  useEffect(() => {
    const updateHeights = () => {
      if (headerRef.current && tabsRef.current && modalRef.current) {
        const headerHeight = headerRef.current.offsetHeight;
        const tabsHeight = tabsRef.current.offsetHeight;
        modalRef.current.style.setProperty('--modal-header-height', `${headerHeight}px`);
        modalRef.current.style.setProperty('--modal-tabs-height', `${tabsHeight}px`);
      }
    };

    updateHeights();
    window.addEventListener('resize', updateHeights);
    return () => window.removeEventListener('resize', updateHeights);
  }, [location.name]);

  const [selectedDay, setSelectedDay] = useState<string>('Monday');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [aisleModalOpen, setAisleModalOpen] = useState<string | null>(null);
  const [viewModeBeforeAisle, setViewModeBeforeAisle] = useState<'map' | 'list' | 'categories'>('list');
  const [viewMode, setViewMode] = useState<'map' | 'list' | 'categories'>('list');
  const [mapZoom, setMapZoom] = useState(0.85);
  const [mapPan, setMapPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [pinchStart, setPinchStart] = useState<{ distance: number; center: { x: number; y: number } } | null>(null);
  const [lastTouchDistance, setLastTouchDistance] = useState<number | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapWrapperRef = useRef<HTMLDivElement>(null);
  const isInteractingRef = useRef(false);
  const [isInteracting, setIsInteracting] = useState(false);
  const rafIdRef = useRef<number | null>(null);
  // Use refs for smooth updates without re-renders during interaction
  const panRef = useRef({ x: 0, y: 0 });
  const zoomRef = useRef(0.85);

  useEffect(() => {
    eventLogger('location_tab', { location: location.name, tab: `${activeTab} ${viewMode}` });
  }, [viewMode]);
  // Sync refs with initial state
  useEffect(() => {
    panRef.current = mapPan;
    zoomRef.current = mapZoom;
  }, []);

  // Calculate initial zoom to fit entire store rectangle in viewport
  const calculateFitZoom = useCallback(() => {
    if (mapContainerRef.current) {
      const container = mapContainerRef.current;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // Store wrapper dimensions: 800px width + 8rem (128px) horizontal padding = 928px total
      // Store wrapper dimensions: 600px height + 7rem (112px) vertical padding = 712px total
      const storeWrapperWidth = 928; // 800 + (4rem * 2)
      const storeWrapperHeight = 712; // 600 + (3.5rem * 2)

      // Calculate zoom to fit with padding (80% of container to leave comfortable margin)
      const widthZoom = (containerWidth * 0.8) / storeWrapperWidth;
      const heightZoom = (containerHeight * 0.8) / storeWrapperHeight;

      // Use the smaller zoom to ensure everything fits, with a minimum of 0.25x
      const calculatedZoom = Math.max(0.25, Math.min(widthZoom, heightZoom, 1));

      return calculatedZoom;
    }
    return 0.85; // fallback
  }, []);

  useEffect(() => {
    if (isMapFullscreen) {
      // Use requestAnimationFrame for smooth initial render
      const rafId = requestAnimationFrame(() => {
        const calculatedZoom = calculateFitZoom();
        const initialPan = { x: 0, y: 0 };
        panRef.current = initialPan;
        zoomRef.current = calculatedZoom;
        setMapZoom(calculatedZoom);
        setMapPan(initialPan);
        // Sync DOM immediately
        if (mapWrapperRef.current) {
          mapWrapperRef.current.style.transform = `translate3d(calc(-50% + ${initialPan.x}px), calc(-50% + ${initialPan.y}px), 0) scale3d(${calculatedZoom}, ${calculatedZoom}, 1)`;
        }
      });

      return () => {
        cancelAnimationFrame(rafId);
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
      };
    }
  }, [isMapFullscreen, calculateFitZoom]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, []);

  // Use scroll-to-top utility hook with automatic scrolling on tab changes
  const { scrollToTop } = useScrollToTopOnChange([activeTab]);

  const {
    convertedCurrentDateTime,
    setConvertedCurrentDateTime,
    selectedDateTimes,
    setSelectedDateTimes,
    setUpDate,
    setUpAllDates,
    getDayCapacities,
    getCurrentCapacity,
  } = useDateHandler();

  const tabs = [
    { id: 'overview', label: 'Overview', icon: MapPin },
    { id: 'busyness', label: 'Crowds', icon: Clock },
    { id: 'store-layout', label: 'Store Layout', icon: Layout },
    { id: 'sales', label: 'Sales', icon: Tag },
    { id: 'food', label: 'Food Court', icon: Utensils },
    { id: 'services', label: 'Services', icon: ShoppingBag },
    { id: 'fuel', label: 'Fuel Station', icon: Fuel },
    { id: 'traffic', label: 'Traffic', icon: Camera },
    { id: 'parking', label: 'Parking', icon: Car },
  ];

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    const currentDateTime = getLocalCurrentDateTime(location.latitude, location.longitude);
    setConvertedCurrentDateTime(setUpDate(currentDateTime.dateTime));
    setUpAllDates(location.capacities);
    setSelectedDay(currentDateTime.day);
  }, []);

  useEffect(() => {
    setSelectedDateTimes(getDayCapacities(selectedDay));
  }, [selectedDay]);

  const getParkTime = (capacity: number) => {
    if (capacity < 40) return '< 1 min';
    if (capacity < 70) return '1 - 3 mins';
    if (capacity < 85) return '3 - 5 mins';
    if (capacity < 95) return '5 - 10 mins';
    return '> 10 mins';
  };

  const getBusynessColor = (busyness: number): string => {
    if (busyness < 30) return '#4CAF50'; // Green - Low busyness
    if (busyness < 60) return '#FF9800'; // Orange - Medium busyness
    if (busyness < 80) return '#FF5722'; // Red-Orange - High busyness
    return '#F44336'; // Red - Very high busyness
  };

  const getBusynessLevel = (busyness: string) => {
    switch (busyness) {
      case 'low':
        return 'Low';
      case 'medium':
        return 'Medium';
      case 'high':
        return 'High';
      case 'Store Closed':
        return 'Store Closed';
      default:
        return 'Unknown';
    }
  };

  const getCurrentBusyness = () => {
    const dayData = location.capacities[convertedCurrentDateTime];
    if (!dayData) return 'Store Closed';
  };

  // Aisle data for each zone
  const getAisleData = (zoneKey: string) => {
    const aisleData: Record<
      string,
      { rows: Array<{ number: string; items: string }>; fridges?: Array<{ label: string; items: string }> }
    > = {
      'back-left': {
        rows: [
          { number: '327', items: 'Tonic water, drinks' },
          { number: '326', items: 'Energy drinks, Gatorade' },
          { number: '325', items: 'Soft drinks' },
          { number: '324', items: 'Soft drinks' },
          { number: '323', items: 'Soft drinks' },
          { number: '322', items: 'Dishwashing liquid' },
          { number: '321', items: 'Cleaning wipes, cloths, disinfectant, BBQ cleaner, bleach, BAM' },
          { number: '320', items: 'Laundry liquid' },
          { number: '319', items: 'Laundry materials, softener' },
          { number: '318', items: 'Pet food and toys' },
          { number: '317', items: 'Pet food' },
          { number: '316', items: 'Peanut butter, jam, Vegemite, peaches' },
          { number: '315', items: 'Oats, granola, cereal, coffee, tea' },
          { number: '314', items: 'Asian drinks, packaged juice' },
        ],
      },
      'middle-left': {
        rows: [
          {
            number: '309',
            items: 'Olives, pickles, tomato sauce, pasta sauce, pizza sauce, diced tomatoes, soy sauce',
          },
          { number: '310', items: 'Sauces, aioli' },
          { number: '311', items: 'Oil, vinegar, sugar, nuts' },
          { number: '312', items: 'Seasoning, stock, chocolate, flour' },
          { number: '313', items: 'Rice, chick peas, lentils, pasta' },
        ],
        fridges: [
          { label: 'F1', items: 'Cheese, cream cheese, yoghurt, butter' },
          { label: 'F2', items: 'Bacon, salami, cream, milk, juice' },
          { label: 'F3', items: 'Cold soft drinks' },
          { label: 'F5', items: 'Frozen berries, fries, frozen veges, pizza, pies, frozen foods, ice cream' },
          { label: 'F6', items: 'Dumplings, Thai, prawns, fish, beef, chicken' },
        ],
      },
      'middle-right': {
        rows: [
          { number: '100', items: 'Home security' },
          { number: '101', items: 'Dental, shaving, hair' },
          { number: '102', items: 'Vacuum, baskets, fans' },
          { number: '103', items: 'Microwave, air fryer, blender' },
          { number: '104', items: 'Coffee, coffee machine, kettle, toaster, water filter, vacuum seal' },
          { number: '105', items: 'Bowls, plates, cheese board, cutting board, kitchen, cutlery' },
          { number: '106', items: 'Pots and pans, plastics, food storage bottles, jars' },
          { number: '107', items: 'Toilet, shower head, sink, curtain rod, flooring, mirrors, light bulbs' },
          { number: '108', items: 'Lights, ceiling fans, solar lights, outdoor lights' },
        ],
      },
      'front-left': {
        rows: [
          { number: '300-302', items: 'Alcohol' },
          { number: '303', items: 'Supplements' },
          { number: '304', items: 'Medicine, health, band aid, first aid' },
          { number: '305', items: 'Protein powder, bars, drinks' },
          { number: '306', items: 'Toothpaste, dental, makeup, pads' },
          { number: '307', items: 'Shaving, deodorant, sunscreen, moisturiser' },
          { number: '308', items: 'Soap, hand wash, body wash' },
        ],
      },
    };
    return aisleData[zoneKey] || null;
  };

  const getZoneTitle = (zoneKey: string) => {
    const titles: Record<string, string> = {
      'back-left': 'Household Essentials',
      'middle-left': 'Pantry & Fridges',
      'middle-right': 'Home & Kitchen',
      'front-left': 'Health & Wellness',
    };
    return titles[zoneKey] || '';
  };

  const getZoneAisleRange = (zoneKey: string) => {
    const ranges: Record<string, string> = {
      'back-left': 'Aisles 314-327',
      'middle-left': 'Aisles 309-313 & Fridges F1-F6',
      'middle-right': 'Aisles 100-108',
      'front-left': 'Aisles 300-308',
    };
    return ranges[zoneKey] || '';
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: `Check out ${location.name} on VibeCheck!`,
          text: `I found this great Costco location with ${getCurrentCapacity()}% crowd level.`,
          url: window.location.href,
        })
        .catch((error) => {
          // Silently handle share cancellation or errors
          if (error.name !== 'AbortError') {
            console.log('Share error:', error);
          }
        });
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const renderOverviewTab = () => (
    <motion.div
      key="overview"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="tab-content"
    >
      <div className="costco-overview-section">
        {/* Crowd Level Chart */}
        <CrowdLevelChart
          convertedCurrentDateTime={convertedCurrentDateTime}
          getDayCapacities={getDayCapacities}
          style={{ marginTop: 0 }}
        />

        {/* Sales Card */}
        <div className="sales-overview-card">
          <button className="sales-overview-item sales-overview-all" onClick={() => setActiveTab('sales')}>
            <Tag size={20} />
            <div className="sales-overview-content">
              <span className="sales-overview-title">All Sales</span>
            </div>
            <ChevronRight size={18} />
          </button>
        </div>

        {/* View Store Layout Card */}
        <button className="store-layout-link-btn" onClick={() => setActiveTab('store-layout')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="3" y1="9" x2="21" y2="9"></line>
            <line x1="9" y1="21" x2="9" y2="9"></line>
          </svg>
          <div className="store-layout-link-content">
            <span className="store-layout-link-title">View Store Layout</span>
            <span className="store-layout-link-desc">Interactive map with aisle locations and categories</span>
          </div>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>

        {/* Suggested Times to Visit */}
        {(() => {
          if (!getDayCapacities) {
            return null;
          }

          const todayData = getDayCapacities(convertedCurrentDateTime);

          if (todayData.length === 0) return null;

          // Filter to only >0% values
          const validData = todayData.filter(([, capacity]) => Number(capacity) > 0);

          if (validData.length === 0) return null;

          // Get current time from convertedCurrentDateTime
          const [currentDatePart, currentTimePart] = convertedCurrentDateTime.split(' ');
          const currentHour = parseInt(currentTimePart.split(':')[0]);
          const currentMinute = parseInt(currentTimePart.split(':')[1]);

          // Filter times that are after current time (same day only)
          const futureTimes = validData.filter(([datetime]) => {
            const [datePart, timePart] = datetime.split(' ');
            const hour = parseInt(timePart.split(':')[0]);
            const minute = parseInt(timePart.split(':')[1]);

            // Only same day
            if (datePart !== currentDatePart) return false;

            // Check if time is after current time
            const timeInMinutes = hour * 60 + minute;
            const currentTimeInMinutes = currentHour * 60 + currentMinute;

            return timeInMinutes > currentTimeInMinutes;
          });

          if (futureTimes.length === 0) {
            // Show "No recommendations" message
            return (
              <div className="next-best-times-card">
                <div className="next-best-times-header">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <div>
                    <h4>Suggested Times to Visit</h4>
                    <p>Quietest times to visit from now</p>
                  </div>
                </div>
                <div className="no-recommendations-message">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <p>No more times available today</p>
                  <span>Plan ahead for the rest of the week</span>
                  <button
                    className="view-week-btn"
                    onClick={() => {
                      // Set the active tab first
                      setActiveTab('busyness');
                      // Click the busyness tab button to ensure it's active
                      const crowdsTab = document.querySelector('[data-tab="busyness"]') as HTMLElement;
                      if (crowdsTab) {
                        crowdsTab.click();
                      }
                      // Longer delay to ensure BusynessTab component is rendered and sub-tabs are available
                      setTimeout(() => {
                        const crowdAnalyserBtn = document.querySelector('.busyness-sub-tab:nth-child(1)') as HTMLElement;
                        if (crowdAnalyserBtn) {
                          crowdAnalyserBtn.click();
                        } else {
                          // Retry after another delay if button not found
                          setTimeout(() => {
                            const retryBtn = document.querySelector('.busyness-sub-tab:nth-child(1)') as HTMLElement;
                            if (retryBtn) retryBtn.click();
                          }, 200);
                        }
                      }, 300);
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    View This Week's Schedule
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </div>
              </div>
            );
          }

          // Sort by capacity and get 2 least busy upcoming times
          const nextBestTimes = futureTimes.sort(([, a], [, b]) => Number(a) - Number(b)).slice(0, 2);

          const formatTime = (datetime: string) => {
            const timePart = datetime.split(' ')[1];
            return timePart.substring(0, 5);
          };

          const getCapacityColor = (capacity: number) => {
            if (capacity < 30) return '#10b981';
            if (capacity < 60) return '#f59e0b';
            if (capacity < 80) return '#fb923c';
            return '#ef4444';
          };

          return (
            <div className="next-best-times-card">
              <div className="next-best-times-header">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <div>
                  <h4>Suggested Times to Visit</h4>
                  <p>Quietest times to visit from now</p>
                </div>
              </div>

              <div className="next-times-chips">
                {nextBestTimes.map(([time, capacity]) => (
                  <div key={time} className="time-chip best">
                    <span className="chip-time">{formatTime(time)}</span>
                    <span className="chip-capacity">{capacity}%</span>
                    <div
                      className="chip-bar"
                      style={{
                        width: `${capacity}%`,
                        backgroundColor: getCapacityColor(Number(capacity)),
                      }}
                    ></div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Plan Your Perfect Visit Button */}
        <button
          className="planner-link-btn"
          onClick={() => {
            // Set the active tab first
            setActiveTab('busyness');
            // Click the busyness tab button to ensure it's active
            const crowdsTab = document.querySelector('[data-tab="busyness"]') as HTMLElement;
            if (crowdsTab) {
              crowdsTab.click();
            }
            // Longer delay to ensure BusynessTab component is rendered and sub-tabs are available
            setTimeout(() => {
              const plannerBtn = document.querySelector('.busyness-sub-tab:nth-child(2)') as HTMLElement;
              if (plannerBtn) {
                plannerBtn.click();
              } else {
                // Retry after another delay if button not found
                setTimeout(() => {
                  const retryBtn = document.querySelector('.busyness-sub-tab:nth-child(2)') as HTMLElement;
                  if (retryBtn) retryBtn.click();
                }, 200);
              }
            }, 300);
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          <div className="planner-link-content">
            <span className="planner-link-title">Plan Your Perfect Visit</span>
            <span className="planner-link-desc">Use advanced filters to find your ideal time</span>
          </div>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>

        {/* Get Directions Button */}
        <button
          className="directions-button"
          onClick={() => {
            const lat = location?.latitude;
            const lon = location?.longitude;
            if (lat && lon) {
              const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`;
              console.log('🗺️ Opening Google Maps with URL:', googleMapsUrl);
              window.open(googleMapsUrl, '_blank');
            } else {
              console.error('❌ No coordinates available for directions');
              alert('Location coordinates not available for directions');
            }
          }}
          title="Open Google Maps with directions to this location"
        >
          <Navigation size={18} />
          <span>Get Directions</span>
        </button>

        <div className="costco-details-grid">
          <div className="detail-card">
            <h3>Available Services</h3>
            <div className="services-list">
              {location.pharmacy_available && <span className="service-tag">Pharmacy</span>}
              {location.optical_available && <span className="service-tag">Optical</span>}
              {location.tyre_centre_available && <span className="service-tag">Tyre Centre</span>}
              {location.hearing_aids_available && <span className="service-tag">Hearing Aids</span>}
              {location.photo_centre && <span className="service-tag">Photo Centre</span>}
              {location.click_and_collect && <span className="service-tag">Click & Collect</span>}
            </div>
          </div>

          <div className="detail-card">
            <h3>Specialty Sections</h3>
            <div className="specialty-sections">
              {location.specialty_sections && Array.isArray(location.specialty_sections) ? (
                location.specialty_sections.map((section, index) => (
                  <span key={index} className="specialty-tag">
                    {section}
                  </span>
                ))
              ) : (
                <span className="no-data">No specialty sections available</span>
              )}
            </div>
          </div>

          <div className="detail-card">
            <h3>Transport Access</h3>
            <p className="transport-info">{location.public_transport_access}</p>
          </div>
        </div>

        <div className="costco-tips-section">
          <h3>Pro Tips</h3>
          <div className="tips-grid">
            <div className="tip-card">
              <h4>Restock Tips</h4>
              <p>{location.restock_tips}</p>
            </div>
            <div className="tip-card">
              <h4>Trolley Locations</h4>
              <p>{location.trolley_location_tips}</p>
            </div>
            <div className="tip-card">
              <h4>Checkout Peak Times</h4>
              <p>{location.checkout_peak_times}</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const renderFoodTab = () => {
    const menuItems = [
      { name: 'Pizza - Slice', price: '$2.99', description: 'Cheese, Pepperoni, BBQ Meat' },
      { name: 'Pizza - Whole', price: '$15.99', description: 'Cheese, Pepperoni, BBQ Meat' },
      { name: 'Hot Dog and Soda', price: '$1.99', description: 'All pork hotdog with 590ml soda and refill' },
      { name: 'Aussie Steak Pie', price: '$7.99', description: '' },
      { name: 'Chicken Tenders', price: '$8.99', description: '' },
      { name: 'Sundae', price: '$2.99', description: '' },
      { name: 'Soda', price: '$0.99', description: 'With refill' },
      { name: 'Mango Smoothie or Bubble Tea', price: '$3.99', description: '' },
    ];

    return (
      <motion.div
        key="food"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="tab-content"
      >
        <div className="costco-food-section">
          {/* Menu & Prices Section - First */}
          <div className="menu-section">
            <h3>Menu & Prices</h3>
            <div className="menu-grid">
              {menuItems.map((item, index) => (
                <div key={index} className="menu-item">
                  <div className="menu-item-header">
                    <div className="menu-item-name">{item.name}</div>
                    <div className="menu-item-price">{item.price}</div>
                  </div>
                  {item.description && <div className="menu-item-description">{item.description}</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Food Court Location & Hours Card - Second */}
          <div className="food-court-info">
            <h3>Food Court</h3>
            <div className="food-details">
              <div className="food-item">
                <span className="food-label">Location:</span>
                <span className="food-value">{location.food_court?.location || 'Near exit'}</span>
              </div>
              <div className="food-item">
                <span className="food-label">Hours:</span>
                <span className="food-value">{location.food_court?.hours || 'Store hours'}</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const renderStoreLayoutTab = () => (
    <motion.div
      key="store-layout"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="tab-content"
    >
      <div className="costco-store-layout-section">
        {/* Interactive Store Layout */}
        <div className="experimental-store-mapper">
          <div className="mapper-header">
            <h4>Interactive Store Layout</h4>
          </div>
          <p className="mapper-description">
            Navigate the store with our detailed aisle-by-aisle guide. Tap any section to see detailed row information.
          </p>

          {/* View Toggle */}
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <Layout size={16} />
              <span>List</span>
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'categories' ? 'active' : ''}`}
              onClick={() => setViewMode('categories')}
            >
              <ShoppingBag size={16} />
              <span>Categories</span>
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'map' ? 'active' : ''}`}
              onClick={() => {
                console.log('🗺️ Map button clicked');
                setViewMode('map');
                setIsMapFullscreen(true);
                console.log('🗺️ Setting isMapFullscreen to true');
              }}
            >
              <MapPin size={16} />
              <span>Map</span>
            </button>
          </div>

          {/* Store Layout - Mobile List View */}
          <div className={`store-layout-mobile-list ${viewMode === 'list' ? 'active' : ''}`}>
            {/* Front Row - Listed First (Near Entrance) */}
            <div className="store-row-section">
              <div className="row-section-header">
                <div className="row-position-badge front-badge">FRONT</div>
                <h5>Front of Store (Near Entrance)</h5>
              </div>
              <div className="row-zones">
                <div
                  className="mobile-zone-card clickable-zone-card"
                  onClick={() => {
                    setViewModeBeforeAisle(viewMode);
                    setAisleModalOpen('front-left');
                  }}
                >
                  <div className="mobile-zone-header">
                    <Pill className="zone-icon" size={20} />
                    <div className="zone-position">Left</div>
                    <ChevronRight className="clickable-indicator" size={20} />
                  </div>
                  <h6>Health & Wellness</h6>
                  <p className="mobile-zone-desc">Alcohol, supplements, health products</p>
                  <div className="mobile-zone-aisles">Aisles 300-308</div>
                  <div className="clickable-hint">
                    <Info size={14} />
                    <span>Tap for detailed aisle information</span>
                  </div>
                </div>

                <div className="mobile-zone-card">
                  <div className="mobile-zone-header">
                    <Gamepad2 className="zone-icon" size={20} />
                    <div className="zone-position">Center</div>
                  </div>
                  <h6>Toys & Snacks</h6>
                  <p className="mobile-zone-desc">Children's toys & packaged food</p>
                </div>

                <div className="mobile-zone-card">
                  <div className="mobile-zone-header">
                    <Laptop className="zone-icon" size={20} />
                    <div className="zone-position">Right</div>
                  </div>
                  <h6>Electronics & Tech</h6>
                  <p className="mobile-zone-desc">Technology, entertainment, jewellery</p>
                </div>
              </div>
            </div>

            {/* Middle Row */}
            <div className="store-row-section">
              <div className="row-section-header">
                <div className="row-position-badge middle-badge">MIDDLE</div>
                <h5>Middle of Store</h5>
              </div>
              <div className="row-zones">
                <div
                  className="mobile-zone-card clickable-zone-card"
                  onClick={() => {
                    setViewModeBeforeAisle(viewMode);
                    setAisleModalOpen('middle-left');
                  }}
                >
                  <div className="mobile-zone-header">
                    <ShoppingBag className="zone-icon" size={20} />
                    <div className="zone-position">Left</div>
                    <ChevronRight className="clickable-indicator" size={20} />
                  </div>
                  <h6>Pantry & Fridges</h6>
                  <p className="mobile-zone-desc">Dry goods, dairy, frozen foods</p>
                  <div className="mobile-zone-aisles">Aisles 309-313 & Fridges F1-F6</div>
                  <div className="clickable-hint">
                    <Info size={14} />
                    <span>Tap for detailed aisle information</span>
                  </div>
                </div>

                <div className="mobile-zone-card">
                  <div className="mobile-zone-header">
                    <Home className="zone-icon" size={20} />
                    <div className="zone-position">Center</div>
                  </div>
                  <h6>Home & Outdoor</h6>
                  <p className="mobile-zone-desc">Clothes, BBQ, garden, pools</p>
                </div>

                <div
                  className="mobile-zone-card clickable-zone-card"
                  onClick={() => {
                    setViewModeBeforeAisle(viewMode);
                    setAisleModalOpen('middle-right');
                  }}
                >
                  <div className="mobile-zone-header">
                    <Home className="zone-icon" size={20} />
                    <div className="zone-position">Right</div>
                    <ChevronRight className="clickable-indicator" size={20} />
                  </div>
                  <h6>Home & Kitchen</h6>
                  <p className="mobile-zone-desc">Appliances, kitchenware, lighting</p>
                  <div className="mobile-zone-aisles">Aisles 100-108</div>
                  <div className="clickable-hint">
                    <Info size={14} />
                    <span>Tap for detailed aisle information</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Back Row */}
            <div className="store-row-section">
              <div className="row-section-header">
                <div className="row-position-badge back-badge">BACK</div>
                <h5>Back of Store</h5>
              </div>
              <div className="row-zones">
                <div
                  className="mobile-zone-card clickable-zone-card"
                  onClick={() => {
                    setViewModeBeforeAisle(viewMode);
                    setAisleModalOpen('back-left');
                  }}
                >
                  <div className="mobile-zone-header">
                    <Package className="zone-icon" size={20} />
                    <div className="zone-position">Left</div>
                    <ChevronRight className="clickable-indicator" size={20} />
                  </div>
                  <h6>Household Essentials</h6>
                  <p className="mobile-zone-desc">Cleaning, beverages, pet food & pantry</p>
                  <div className="mobile-zone-aisles">Aisles 314-327</div>
                  <div className="clickable-hint">
                    <Info size={14} />
                    <span>Tap for detailed aisle information</span>
                  </div>
                </div>

                <div className="mobile-zone-card">
                  <div className="mobile-zone-header">
                    <ChefHat className="zone-icon" size={20} />
                    <div className="zone-position">Center</div>
                  </div>
                  <h6>Fresh Food Court</h6>
                  <p className="mobile-zone-desc">Bakery, meat & deli</p>
                </div>

                <div className="mobile-zone-card">
                  <div className="mobile-zone-header">
                    <Apple className="zone-icon" size={20} />
                    <div className="zone-position">Right</div>
                  </div>
                  <h6>Fresh Produce</h6>
                  <p className="mobile-zone-desc">Fruits & vegetables</p>
                </div>
              </div>
            </div>
          </div>

          {/* Categories View */}
          <div className={`store-categories-view ${viewMode === 'categories' ? 'active' : ''}`}>
            <div className="categories-grid">
              <div className="category-card">
                <div className="category-icon-wrapper">
                  <Apple className="category-icon" size={24} />
                </div>
                <div className="category-content">
                  <h6 className="category-name">Fresh Produce</h6>
                  <p className="category-location">Back Right</p>
                </div>
              </div>

              <div className="category-card">
                <div className="category-icon-wrapper">
                  <Beef className="category-icon" size={24} />
                </div>
                <div className="category-content">
                  <h6 className="category-name">Meat & Deli</h6>
                  <p className="category-location">Back Center</p>
                </div>
              </div>

              <div className="category-card">
                <div className="category-icon-wrapper">
                  <ChefHat className="category-icon" size={24} />
                </div>
                <div className="category-content">
                  <h6 className="category-name">Bakery</h6>
                  <p className="category-location">Back Center</p>
                </div>
              </div>

              <div className="category-card">
                <div className="category-icon-wrapper">
                  <Milk className="category-icon" size={24} />
                </div>
                <div className="category-content">
                  <h6 className="category-name">Dairy & Eggs</h6>
                  <p className="category-location">Middle Left</p>
                </div>
              </div>

              <div className="category-card">
                <div className="category-icon-wrapper">
                  <IceCream className="category-icon" size={24} />
                </div>
                <div className="category-content">
                  <h6 className="category-name">Frozen Foods</h6>
                  <p className="category-location">Middle Left</p>
                </div>
              </div>

              <div className="category-card">
                <div className="category-icon-wrapper">
                  <ShoppingBag className="category-icon" size={24} />
                </div>
                <div className="category-content">
                  <h6 className="category-name">Pantry & Dry Goods</h6>
                  <p className="category-location">Middle Left</p>
                </div>
              </div>

              <div className="category-card">
                <div className="category-icon-wrapper">
                  <Package className="category-icon" size={24} />
                </div>
                <div className="category-content">
                  <h6 className="category-name">Household Essentials</h6>
                  <p className="category-location">Back Left</p>
                </div>
              </div>

              <div className="category-card">
                <div className="category-icon-wrapper">
                  <Droplets className="category-icon" size={24} />
                </div>
                <div className="category-content">
                  <h6 className="category-name">Cleaning Supplies</h6>
                  <p className="category-location">Back Left</p>
                </div>
              </div>

              <div className="category-card">
                <div className="category-icon-wrapper">
                  <Wine className="category-icon" size={24} />
                </div>
                <div className="category-content">
                  <h6 className="category-name">Beverages</h6>
                  <p className="category-location">Back Left</p>
                </div>
              </div>

              <div className="category-card">
                <div className="category-icon-wrapper">
                  <Pill className="category-icon" size={24} />
                </div>
                <div className="category-content">
                  <h6 className="category-name">Health & Wellness</h6>
                  <p className="category-location">Front Left</p>
                </div>
              </div>

              <div className="category-card">
                <div className="category-icon-wrapper">
                  <Home className="category-icon" size={24} />
                </div>
                <div className="category-content">
                  <h6 className="category-name">Home & Kitchen</h6>
                  <p className="category-location">Middle Right</p>
                </div>
              </div>

              <div className="category-card">
                <div className="category-icon-wrapper">
                  <Laptop className="category-icon" size={24} />
                </div>
                <div className="category-content">
                  <h6 className="category-name">Electronics</h6>
                  <p className="category-location">Front Right</p>
                </div>
              </div>

              <div className="category-card">
                <div className="category-icon-wrapper">
                  <Gamepad2 className="category-icon" size={24} />
                </div>
                <div className="category-content">
                  <h6 className="category-name">Toys & Games</h6>
                  <p className="category-location">Front Center</p>
                </div>
              </div>

              <div className="category-card">
                <div className="category-icon-wrapper">
                  <Shirt className="category-icon" size={24} />
                </div>
                <div className="category-content">
                  <h6 className="category-name">Clothing</h6>
                  <p className="category-location">Middle Center</p>
                </div>
              </div>

              <div className="category-card">
                <div className="category-icon-wrapper">
                  <Trees className="category-icon" size={24} />
                </div>
                <div className="category-content">
                  <h6 className="category-name">Outdoor & Garden</h6>
                  <p className="category-location">Middle Center</p>
                </div>
              </div>
            </div>
          </div>

          {/* Store Layout - Desktop Map View */}
          <div className={`store-layout-wrapper ${viewMode === 'map' && !isMapFullscreen ? 'active' : ''}`}>
            {/* Desktop Static Map */}
            <div className="store-floor-plan-wrapper">
              {/* Position Labels */}
              <div className="position-label position-label-top">BACK</div>
              <div className="position-label position-label-bottom">FRONT</div>
              <div className="position-label position-label-left">LEFT</div>
              <div className="position-label position-label-right">RIGHT</div>

              {/* Store Boundary Rectangle */}
              <div className="store-floor-plan">
                {/* Store Walls/Perimeter */}
                <div className="store-wall store-wall-top"></div>
                <div className="store-wall store-wall-bottom"></div>
                <div className="store-wall store-wall-left"></div>
                <div className="store-wall store-wall-right"></div>

                {/* Aisle Pathways */}
                <div className="aisle-pathway aisle-vertical aisle-left"></div>
                <div className="aisle-pathway aisle-vertical aisle-right"></div>
                <div className="aisle-pathway aisle-horizontal aisle-top"></div>
                <div className="aisle-pathway aisle-horizontal aisle-bottom"></div>

                {/* Grid Container */}
                <div className="store-layout-mapper-grid">
                  {/* BACK SECTION */}
                  <div className="store-section-header store-section-back">BACK OF STORE</div>
                  {/* BACK ROW (Top) */}
                  <div className="layout-zone-card back-left">
                    <div className="zone-card-header">
                      <Package className="zone-icon" size={20} />
                      <div className="zone-indicator back-left-indicator"></div>
                      <h5>Household Essentials</h5>
                    </div>
                    <p className="zone-description">Cleaning supplies, beverages, pet food & pantry staples</p>
                    <div className="zone-summary">
                      <div className="summary-item">
                        <Droplets size={14} />
                        <span>Cleaning & Laundry</span>
                      </div>
                      <div className="summary-item">
                        <ShoppingCart size={14} />
                        <span>Beverages & Drinks</span>
                      </div>
                      <div className="summary-item">
                        <Package size={14} />
                        <span>Pet Supplies</span>
                      </div>
                      <div className="summary-item">
                        <Box size={14} />
                        <span>Pantry Items</span>
                      </div>
                    </div>
                    <button
                      className="expand-rows-button"
                      onClick={() => {
                        setViewModeBeforeAisle('map');
                        setIsMapFullscreen(false);
                        setAisleModalOpen('back-left');
                      }}
                    >
                      <span>View Aisles 314-327</span>
                      <ChevronDown size={16} />
                    </button>
                  </div>

                  <div className="layout-zone-card back-middle">
                    <div className="zone-card-header">
                      <ChefHat className="zone-icon" size={20} />
                      <div className="zone-indicator back-middle-indicator"></div>
                      <h5>Fresh Food Court</h5>
                    </div>
                    <p className="zone-description">Freshly baked goods, premium meats & deli selections</p>
                    <div className="zone-summary">
                      <div className="summary-item">
                        <Sparkles size={14} />
                        <span>Bakery</span>
                      </div>
                      <div className="summary-item">
                        <Beef size={14} />
                        <span>Meat</span>
                      </div>
                      <div className="summary-item">
                        <UtensilsCrossed size={14} />
                        <span>Deli</span>
                      </div>
                    </div>
                  </div>

                  <div className="layout-zone-card back-right">
                    <div className="zone-card-header">
                      <Apple className="zone-icon" size={20} />
                      <div className="zone-indicator back-right-indicator"></div>
                      <h5>Fresh Produce</h5>
                    </div>
                    <p className="zone-description">Farm-fresh fruits & vegetables</p>
                    <div className="zone-summary">
                      <div className="summary-item">
                        <Apple size={14} />
                        <span>Fresh Produce</span>
                      </div>
                      <div className="summary-item">
                        <Apple size={14} />
                        <span>Fruit</span>
                      </div>
                    </div>
                  </div>

                  {/* MIDDLE SECTION */}
                  <div className="store-section-header store-section-middle">MIDDLE OF STORE</div>
                  {/* MIDDLE ROW */}
                  <div className="layout-zone-card middle-left">
                    <div className="zone-card-header">
                      <ShoppingBag className="zone-icon" size={20} />
                      <div className="zone-indicator middle-left-indicator"></div>
                      <h5>Pantry & Fridges</h5>
                    </div>
                    <p className="zone-description">Dry goods, condiments, dairy, frozen foods & fresh meats</p>
                    <div className="zone-summary">
                      <div className="summary-item">
                        <Package size={14} />
                        <span>Pantry Aisles 309-313</span>
                      </div>
                      <div className="summary-item">
                        <Refrigerator size={14} />
                        <span>Fridges F1-F6</span>
                      </div>
                    </div>
                    <button
                      className="expand-rows-button"
                      onClick={() => {
                        setViewModeBeforeAisle('map');
                        setIsMapFullscreen(false);
                        setAisleModalOpen('middle-left');
                      }}
                    >
                      <span>View Aisles & Fridges</span>
                      <ChevronDown size={16} />
                    </button>
                  </div>

                  <div className="layout-zone-card middle-middle">
                    <div className="zone-card-header">
                      <Home className="zone-icon" size={20} />
                      <div className="zone-indicator middle-middle-indicator"></div>
                      <h5>Home & Outdoor</h5>
                    </div>
                    <p className="zone-description">Apparel, outdoor living & seasonal items</p>
                    <div className="zone-summary">
                      <div className="summary-item">
                        <Shirt size={14} />
                        <span>Clothes</span>
                      </div>
                      <div className="summary-item">
                        <Utensils size={14} />
                        <span>Outdoor BBQ</span>
                      </div>
                      <div className="summary-item">
                        <Trees size={14} />
                        <span>Garden</span>
                      </div>
                      <div className="summary-item">
                        <Waves size={14} />
                        <span>Pools / Cabana</span>
                      </div>
                    </div>
                  </div>

                  <div className="layout-zone-card middle-right">
                    <div className="zone-card-header">
                      <Home className="zone-icon" size={20} />
                      <div className="zone-indicator middle-right-indicator"></div>
                      <h5>Home & Kitchen</h5>
                    </div>
                    <p className="zone-description">Appliances, kitchenware, home security & lighting</p>
                    <div className="zone-summary">
                      <div className="summary-item">
                        <Home size={14} />
                        <span>Aisles 100-108</span>
                      </div>
                    </div>
                    <button
                      className="expand-rows-button"
                      onClick={() => {
                        setViewModeBeforeAisle('map');
                        setIsMapFullscreen(false);
                        setAisleModalOpen('middle-right');
                      }}
                    >
                      <span>View Aisles 100-108</span>
                      <ChevronDown size={16} />
                    </button>
                  </div>

                  {/* FRONT SECTION */}
                  <div className="store-section-header store-section-front">FRONT OF STORE</div>
                  {/* FRONT ROW (Bottom) */}
                  <div className="layout-zone-card front-left">
                    <div className="zone-card-header">
                      <Pill className="zone-icon" size={20} />
                      <div className="zone-indicator front-left-indicator"></div>
                      <h5>Health & Wellness</h5>
                    </div>
                    <p className="zone-description">Alcohol, supplements, health products & personal care</p>
                    <div className="zone-summary">
                      <div className="summary-item">
                        <Wine size={14} />
                        <span>Aisles 300-308</span>
                      </div>
                    </div>
                    <button
                      className="expand-rows-button"
                      onClick={() => {
                        setViewModeBeforeAisle('map');
                        setIsMapFullscreen(false);
                        setAisleModalOpen('front-left');
                      }}
                    >
                      <span>View Aisles 300-308</span>
                      <ChevronDown size={16} />
                    </button>
                  </div>

                  <div className="layout-zone-card front-middle">
                    <div className="zone-card-header">
                      <Gamepad2 className="zone-icon" size={20} />
                      <div className="zone-indicator front-middle-indicator"></div>
                      <h5>Toys & Snacks</h5>
                    </div>
                    <p className="zone-description">Children's toys & packaged food items</p>
                    <div className="zone-summary">
                      <div className="summary-item">
                        <Gamepad2 size={14} />
                        <span>Toys</span>
                      </div>
                      <div className="summary-item">
                        <Box size={14} />
                        <span>Packaged Food</span>
                      </div>
                    </div>
                  </div>

                  <div className="layout-zone-card front-right">
                    <div className="zone-card-header">
                      <Laptop className="zone-icon" size={20} />
                      <div className="zone-indicator front-right-indicator"></div>
                      <h5>Electronics & Tech</h5>
                    </div>
                    <p className="zone-description">Technology, entertainment systems & jewellery</p>
                    <div className="zone-summary">
                      <div className="summary-item">
                        <Laptop size={14} />
                        <span>Technology</span>
                      </div>
                      <div className="summary-item">
                        <Tv size={14} />
                        <span>Home Entertainment</span>
                      </div>
                      <div className="summary-item">
                        <Headphones size={14} />
                        <span>Audio Visual</span>
                      </div>
                      <div className="summary-item">
                        <Laptop size={14} />
                        <span>Computer</span>
                      </div>
                      <div className="summary-item">
                        <Gem size={14} />
                        <span>Jewellery</span>
                      </div>
                    </div>
                  </div>

                  {/* Entrance/Exit/Checkout Cards */}
                  <div className="layout-zone-card checkout-card">
                    <div className="zone-card-header">
                      <CreditCard className="zone-icon" size={20} />
                      <h5>Checkout</h5>
                    </div>
                    <p className="zone-description">Front Left</p>
                  </div>

                  <div className="layout-zone-card exit-card">
                    <div className="zone-card-header">
                      <LogOut className="zone-icon" size={20} />
                      <h5>Exit</h5>
                    </div>
                    <p className="zone-description">Front Middle</p>
                  </div>

                  <div className="layout-zone-card entrance-card">
                    <div className="zone-card-header">
                      <LogIn className="zone-icon" size={20} />
                      <h5>Entrance</h5>
                    </div>
                    <p className="zone-description">Front Right</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mapper-legend">
            <div className="legend-item">
              <div className="legend-icon entrance-icon">
                <LogIn size={20} />
              </div>
              <div className="legend-text">
                <strong>Entrance:</strong> Front Right
              </div>
            </div>
            <div className="legend-item">
              <div className="legend-icon exit-icon">
                <LogOut size={20} />
              </div>
              <div className="legend-text">
                <strong>Exit:</strong> Front Middle
              </div>
            </div>
            <div className="legend-item">
              <div className="legend-icon checkout-icon">
                <CreditCard size={20} />
              </div>
              <div className="legend-text">
                <strong>Checkout:</strong> Front Left
              </div>
            </div>
          </div>

          <p className="mapper-note">
            <strong>Note:</strong> This is an experimental feature. Layout shows approximate section locations. Tap
            "View Aisles" to see detailed row information.
          </p>
        </div>

        <div className="store-navigation-tips">
          <h4>Navigation Tips</h4>
          <div className="tips-grid">
            <div className="tip-card">
              <h5>🛒 Shopping Flow</h5>
              <p>
                Start with fresh produce at the front, work your way through refrigerated items, then frozen goods, and
                finish with packaged items.
              </p>
            </div>
            <div className="tip-card">
              <h5>📍 Section Markers</h5>
              <p>
                Look for overhead signs and floor markings that indicate each section. Staff are always available to
                help direct you.
              </p>
            </div>
            <div className="tip-card">
              <h5>🕐 Best Times</h5>
              <p>
                Visit during off-peak hours for easier navigation and shorter checkout lines. Check the Crowds tab for
                current conditions.
              </p>
            </div>
            <div className="tip-card">
              <h5>🛍️ Trolley Tips</h5>
              <p>{location.trolley_location_tips}</p>
            </div>
          </div>
        </div>

        <div className="store-features">
          <h4>Store Features</h4>
          <div className="features-grid">
            <div className="feature-item">
              <Store className="feature-icon" size={24} />
              <div className="feature-info">
                <h5>Warehouse Type</h5>
                <p>{location.warehouse_type}</p>
              </div>
            </div>
            <div className="feature-item">
              <LogIn className="feature-icon" size={24} />
              <div className="feature-info">
                <h5>Main Entrance</h5>
                <p>{location.entrance_location}</p>
              </div>
            </div>
            <div className="feature-item">
              <ReturnIcon className="feature-icon" size={24} />
              <div className="feature-info">
                <h5>Returns Desk</h5>
                <p>{location.returns_desk_location}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Fullscreen Interactive Map Modal */}
        {createPortal(
          <AnimatePresence mode="wait">
            {isMapFullscreen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="map-fullscreen-overlay"
                  onClick={() => {
                    setIsMapFullscreen(false);
                    setViewMode('list');
                  }}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className="map-fullscreen-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div className="map-fullscreen-header">
                    <div className="map-fullscreen-title">
                      <MapPin size={24} />
                      <h3>Interactive Store Map</h3>
                    </div>
                    <button
                      className="map-fullscreen-close"
                      onClick={() => {
                        setIsMapFullscreen(false);
                        setViewMode('list');
                      }}
                      aria-label="Close map"
                    >
                      <X size={24} />
                    </button>
                  </div>

                  {/* Zoom Controls */}
                  <div className="map-zoom-controls-fullscreen">
                    <button
                      className="zoom-control-btn"
                      onClick={() => {
                        const newZoom = Math.min(zoomRef.current + 0.15, 2);
                        zoomRef.current = newZoom;
                        setMapZoom(newZoom);
                        if (mapWrapperRef.current) {
                          mapWrapperRef.current.style.transform = `translate3d(calc(-50% + ${panRef.current.x}px), calc(-50% + ${panRef.current.y}px), 0) scale3d(${newZoom}, ${newZoom}, 1)`;
                        }
                      }}
                      aria-label="Zoom in"
                    >
                      <ZoomIn size={20} />
                    </button>
                    <button
                      className="zoom-control-btn"
                      onClick={() => {
                        const newZoom = Math.max(zoomRef.current - 0.15, 0.25);
                        zoomRef.current = newZoom;
                        setMapZoom(newZoom);
                        if (mapWrapperRef.current) {
                          mapWrapperRef.current.style.transform = `translate3d(calc(-50% + ${panRef.current.x}px), calc(-50% + ${panRef.current.y}px), 0) scale3d(${newZoom}, ${newZoom}, 1)`;
                        }
                      }}
                      aria-label="Zoom out"
                    >
                      <ZoomOut size={20} />
                    </button>
                    <button
                      className="zoom-control-btn"
                      onClick={() => {
                        // Reset to fit entire store rectangle in viewport
                        const calculatedZoom = calculateFitZoom();
                        const resetPan = { x: 0, y: 0 };
                        panRef.current = resetPan;
                        zoomRef.current = calculatedZoom;
                        setMapZoom(calculatedZoom);
                        setMapPan(resetPan);
                        if (mapWrapperRef.current) {
                          mapWrapperRef.current.style.transform = `translate3d(calc(-50% + ${resetPan.x}px), calc(-50% + ${resetPan.y}px), 0) scale3d(${calculatedZoom}, ${calculatedZoom}, 1)`;
                        }
                      }}
                      aria-label="Reset view"
                    >
                      <RotateCcw size={20} />
                    </button>
                    <div className="zoom-level-indicator">{Math.round(mapZoom * 100)}%</div>
                  </div>

                  {/* Interactive Map Container */}
                  <div
                    ref={mapContainerRef}
                    className={`map-fullscreen-container ${isInteracting ? 'interacting' : ''}`}
                    onWheel={(e) => {
                      e.preventDefault();
                      if (rafIdRef.current) {
                        cancelAnimationFrame(rafIdRef.current);
                      }

                      rafIdRef.current = requestAnimationFrame(() => {
                        const delta = e.deltaY > 0 ? -0.08 : 0.08;
                        const newZoom = Math.max(0.25, Math.min(2, zoomRef.current + delta));
                        zoomRef.current = newZoom;
                        // Direct DOM update for smooth interaction - no React re-render
                        if (mapWrapperRef.current) {
                          mapWrapperRef.current.style.transform = `translate3d(calc(-50% + ${panRef.current.x}px), calc(-50% + ${panRef.current.y}px), 0) scale3d(${newZoom}, ${newZoom}, 1)`;
                        }
                        // Debounce state update to avoid excessive re-renders
                        setTimeout(() => setMapZoom(newZoom), 0);
                      });
                    }}
                    onTouchStart={(e) => {
                      if (e.touches.length === 2) {
                        // Pinch to zoom
                        e.preventDefault();
                        e.stopPropagation();
                        setIsInteracting(true);
                        isInteractingRef.current = true;
                        const touch1 = e.touches[0];
                        const touch2 = e.touches[1];
                        const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
                        const centerX = (touch1.clientX + touch2.clientX) / 2;
                        const centerY = (touch1.clientY + touch2.clientY) / 2;

                        // Store pinch center relative to container
                        if (mapContainerRef.current) {
                          const container = mapContainerRef.current;
                          const rect = container.getBoundingClientRect();
                          const containerCenterX = rect.left + rect.width / 2;
                          const containerCenterY = rect.top + rect.height / 2;
                          const pinchCenterX = centerX - containerCenterX;
                          const pinchCenterY = centerY - containerCenterY;
                          setPinchStart({
                            distance,
                            center: { x: pinchCenterX, y: pinchCenterY },
                          });
                        } else {
                          setPinchStart({ distance, center: { x: centerX, y: centerY } });
                        }
                        setLastTouchDistance(distance);
                        setIsDragging(false);
                      } else if (e.touches.length === 1 && !pinchStart) {
                        // Single touch drag
                        setIsInteracting(true);
                        isInteractingRef.current = true;
                        setIsDragging(true);
                        setDragStart({
                          x: e.touches[0].clientX - panRef.current.x,
                          y: e.touches[0].clientY - panRef.current.y,
                        });
                      }
                    }}
                    onTouchMove={(e) => {
                      if (e.touches.length === 2) {
                        // Pinch to zoom
                        e.preventDefault();
                        e.stopPropagation();
                        isInteractingRef.current = true;

                        if (rafIdRef.current) {
                          cancelAnimationFrame(rafIdRef.current);
                        }

                        rafIdRef.current = requestAnimationFrame(() => {
                          const touch1 = e.touches[0];
                          const touch2 = e.touches[1];
                          const currentDistance = Math.hypot(
                            touch2.clientX - touch1.clientX,
                            touch2.clientY - touch1.clientY
                          );

                          if (lastTouchDistance !== null && lastTouchDistance > 0 && pinchStart) {
                            const scaleChange = currentDistance / lastTouchDistance;
                            const oldZoom = zoomRef.current;
                            const newZoom = Math.max(0.25, Math.min(2, oldZoom * scaleChange));

                            // Calculate pan adjustment to zoom towards pinch center
                            if (mapContainerRef.current && pinchStart.center) {
                              // Get current pinch center in container coordinates
                              const container = mapContainerRef.current;
                              const rect = container.getBoundingClientRect();
                              const containerCenterX = rect.left + rect.width / 2;
                              const containerCenterY = rect.top + rect.height / 2;
                              const currentCenterX = (touch1.clientX + touch2.clientX) / 2 - containerCenterX;
                              const currentCenterY = (touch1.clientY + touch2.clientY) / 2 - containerCenterY;

                              // Calculate the focal point (pinch center) in world coordinates before zoom
                              const focalX = (currentCenterX - panRef.current.x) / oldZoom;
                              const focalY = (currentCenterY - panRef.current.y) / oldZoom;

                              // Calculate where the focal point should be after zoom
                              const newFocalX = focalX * newZoom;
                              const newFocalY = focalY * newZoom;

                              // Adjust pan to keep focal point in same screen position
                              const newPanX = currentCenterX - newFocalX;
                              const newPanY = currentCenterY - newFocalY;

                              panRef.current = { x: newPanX, y: newPanY };
                              zoomRef.current = newZoom;

                              // Direct DOM update for smooth interaction - no React re-render
                              if (mapWrapperRef.current) {
                                mapWrapperRef.current.style.transform = `translate3d(calc(-50% + ${newPanX}px), calc(-50% + ${newPanY}px), 0) scale3d(${newZoom}, ${newZoom}, 1)`;
                              }
                            } else {
                              // Fallback to center zoom if no pinch center
                              zoomRef.current = newZoom;
                              if (mapWrapperRef.current) {
                                mapWrapperRef.current.style.transform = `translate3d(calc(-50% + ${panRef.current.x}px), calc(-50% + ${panRef.current.y}px), 0) scale3d(${newZoom}, ${newZoom}, 1)`;
                              }
                            }
                          }
                          setLastTouchDistance(currentDistance);
                        });
                      } else if (isDragging && e.touches.length === 1 && !pinchStart) {
                        // Single touch drag
                        e.preventDefault();
                        e.stopPropagation();
                        isInteractingRef.current = true;

                        if (rafIdRef.current) {
                          cancelAnimationFrame(rafIdRef.current);
                        }

                        rafIdRef.current = requestAnimationFrame(() => {
                          const newPan = {
                            x: e.touches[0].clientX - dragStart.x,
                            y: e.touches[0].clientY - dragStart.y,
                          };
                          panRef.current = newPan;
                          // Direct DOM update for smooth interaction - no React re-render
                          if (mapWrapperRef.current) {
                            mapWrapperRef.current.style.transform = `translate3d(calc(-50% + ${newPan.x}px), calc(-50% + ${newPan.y}px), 0) scale3d(${zoomRef.current}, ${zoomRef.current}, 1)`;
                          }
                        });
                      }
                    }}
                    onTouchEnd={(e) => {
                      if (rafIdRef.current) {
                        cancelAnimationFrame(rafIdRef.current);
                        rafIdRef.current = null;
                      }
                      // Sync state with refs after interaction
                      setMapPan(panRef.current);
                      setMapZoom(zoomRef.current);
                      setIsInteracting(false);
                      isInteractingRef.current = false;

                      if (e.touches.length === 0) {
                        setIsDragging(false);
                        setPinchStart(null);
                        setLastTouchDistance(null);
                      } else if (e.touches.length === 1) {
                        // Switched from pinch to single touch
                        setPinchStart(null);
                        setLastTouchDistance(null);
                        setIsDragging(true);
                        setDragStart({
                          x: e.touches[0].clientX - panRef.current.x,
                          y: e.touches[0].clientY - panRef.current.y,
                        });
                      }
                    }}
                    onTouchCancel={() => {
                      if (rafIdRef.current) {
                        cancelAnimationFrame(rafIdRef.current);
                        rafIdRef.current = null;
                      }
                      // Sync state with refs after interaction
                      setMapPan(panRef.current);
                      setMapZoom(zoomRef.current);
                      setIsInteracting(false);
                      isInteractingRef.current = false;
                      setIsDragging(false);
                      setPinchStart(null);
                      setLastTouchDistance(null);
                    }}
                    onMouseDown={(e) => {
                      if (e.button === 0) {
                        setIsInteracting(true);
                        isInteractingRef.current = true;
                        setIsDragging(true);
                        setDragStart({
                          x: e.clientX - panRef.current.x,
                          y: e.clientY - panRef.current.y,
                        });
                      }
                    }}
                    onMouseUp={() => {
                      if (rafIdRef.current) {
                        cancelAnimationFrame(rafIdRef.current);
                        rafIdRef.current = null;
                      }
                      // Sync state with refs after interaction
                      setMapPan(panRef.current);
                      setMapZoom(zoomRef.current);
                      setIsInteracting(false);
                      isInteractingRef.current = false;
                      setIsDragging(false);
                    }}
                    onMouseLeave={() => {
                      if (rafIdRef.current) {
                        cancelAnimationFrame(rafIdRef.current);
                        rafIdRef.current = null;
                      }
                      setIsInteracting(false);
                      isInteractingRef.current = false;
                      setIsDragging(false);
                    }}
                    onMouseMove={(e) => {
                      if (isDragging) {
                        if (rafIdRef.current) {
                          cancelAnimationFrame(rafIdRef.current);
                        }

                        rafIdRef.current = requestAnimationFrame(() => {
                          const newPan = {
                            x: e.clientX - dragStart.x,
                            y: e.clientY - dragStart.y,
                          };
                          panRef.current = newPan;
                          // Direct DOM update for smooth interaction - no React re-render
                          if (mapWrapperRef.current) {
                            mapWrapperRef.current.style.transform = `translate3d(calc(-50% + ${newPan.x}px), calc(-50% + ${newPan.y}px), 0) scale3d(${zoomRef.current}, ${zoomRef.current}, 1)`;
                          }
                        });
                      }
                    }}
                  >
                    {/* Position Labels - Fixed to viewport, always visible */}
                    <div className="position-label position-label-top position-label-fixed">BACK</div>
                    <div className="position-label position-label-left position-label-fixed">LEFT</div>
                    <div className="position-label position-label-right position-label-fixed">RIGHT</div>

                    {/* Floor Plan Map Container */}
                    <div
                      ref={mapWrapperRef}
                      className="store-floor-plan-wrapper"
                      style={{
                        transform: `translate3d(calc(-50% + ${mapPan.x}px), calc(-50% + ${mapPan.y}px), 0) scale3d(${mapZoom}, ${mapZoom}, 1)`,
                      }}
                    >
                      {/* Store Boundary Rectangle */}
                      <div className="store-floor-plan">
                        {/* Store Walls/Perimeter */}
                        <div className="store-wall store-wall-top"></div>
                        <div className="store-wall store-wall-bottom"></div>
                        <div className="store-wall store-wall-left"></div>
                        <div className="store-wall store-wall-right"></div>

                        {/* Aisle Pathways */}
                        <div className="aisle-pathway aisle-vertical aisle-left"></div>
                        <div className="aisle-pathway aisle-vertical aisle-right"></div>
                        <div className="aisle-pathway aisle-horizontal aisle-top"></div>
                        <div className="aisle-pathway aisle-horizontal aisle-bottom"></div>

                        {/* Grid Container - Cards contained within boundary */}
                        <div className="store-layout-mapper-grid">
                          {/* BACK SECTION */}
                          <div className="store-section-header store-section-back">BACK OF STORE</div>
                          {/* BACK ROW (Top) */}
                          <div className="layout-zone-card back-left">
                            <div className="zone-card-header">
                              <Package className="zone-icon" size={20} />
                              <div className="zone-indicator back-left-indicator"></div>
                              <h5>Household Essentials</h5>
                            </div>
                            <p className="zone-description">Cleaning supplies, beverages, pet food & pantry staples</p>
                            <div className="zone-summary">
                              <div className="summary-item">
                                <Droplets size={14} />
                                <span>Cleaning & Laundry</span>
                              </div>
                              <div className="summary-item">
                                <ShoppingCart size={14} />
                                <span>Beverages & Drinks</span>
                              </div>
                              <div className="summary-item">
                                <Package size={14} />
                                <span>Pet Supplies</span>
                              </div>
                              <div className="summary-item">
                                <Box size={14} />
                                <span>Pantry Items</span>
                              </div>
                            </div>
                            <button
                              className="expand-rows-button"
                              onClick={() => {
                                setViewModeBeforeAisle('map');
                                setIsMapFullscreen(false);
                                setAisleModalOpen('back-left');
                              }}
                            >
                              <span>View Aisles 314-327</span>
                              <ChevronDown size={16} />
                            </button>
                          </div>

                          <div className="layout-zone-card back-middle">
                            <div className="zone-card-header">
                              <ChefHat className="zone-icon" size={20} />
                              <div className="zone-indicator back-middle-indicator"></div>
                              <h5>Fresh Food Court</h5>
                            </div>
                            <p className="zone-description">Freshly baked goods, premium meats & deli selections</p>
                            <div className="zone-summary">
                              <div className="summary-item">
                                <Sparkles size={14} />
                                <span>Bakery</span>
                              </div>
                              <div className="summary-item">
                                <Beef size={14} />
                                <span>Meat</span>
                              </div>
                              <div className="summary-item">
                                <UtensilsCrossed size={14} />
                                <span>Deli</span>
                              </div>
                            </div>
                          </div>

                          <div className="layout-zone-card back-right">
                            <div className="zone-card-header">
                              <Apple className="zone-icon" size={20} />
                              <div className="zone-indicator back-right-indicator"></div>
                              <h5>Fresh Produce</h5>
                            </div>
                            <p className="zone-description">Farm-fresh fruits & vegetables</p>
                            <div className="zone-summary">
                              <div className="summary-item">
                                <Apple size={14} />
                                <span>Fresh Produce</span>
                              </div>
                              <div className="summary-item">
                                <Apple size={14} />
                                <span>Fruit</span>
                              </div>
                            </div>
                          </div>

                          {/* MIDDLE SECTION */}
                          <div className="store-section-header store-section-middle">MIDDLE OF STORE</div>
                          {/* MIDDLE ROW */}
                          <div className="layout-zone-card middle-left">
                            <div className="zone-card-header">
                              <ShoppingBag className="zone-icon" size={20} />
                              <div className="zone-indicator middle-left-indicator"></div>
                              <h5>Pantry & Fridges</h5>
                            </div>
                            <p className="zone-description">Dry goods, condiments, dairy, frozen foods & fresh meats</p>
                            <div className="zone-summary">
                              <div className="summary-item">
                                <Package size={14} />
                                <span>Pantry Aisles 309-313</span>
                              </div>
                              <div className="summary-item">
                                <Refrigerator size={14} />
                                <span>Fridges F1-F6</span>
                              </div>
                            </div>
                            <button
                              className="expand-rows-button"
                              onClick={() => {
                                setViewModeBeforeAisle('map');
                                setIsMapFullscreen(false);
                                setAisleModalOpen('middle-left');
                              }}
                            >
                              <span>View Aisles & Fridges</span>
                              <ChevronDown size={16} />
                            </button>
                          </div>

                          <div className="layout-zone-card middle-middle">
                            <div className="zone-card-header">
                              <Home className="zone-icon" size={20} />
                              <div className="zone-indicator middle-middle-indicator"></div>
                              <h5>Home & Outdoor</h5>
                            </div>
                            <p className="zone-description">Apparel, outdoor living & seasonal items</p>
                            <div className="zone-summary">
                              <div className="summary-item">
                                <Shirt size={14} />
                                <span>Clothes</span>
                              </div>
                              <div className="summary-item">
                                <Utensils size={14} />
                                <span>Outdoor BBQ</span>
                              </div>
                              <div className="summary-item">
                                <Trees size={14} />
                                <span>Garden</span>
                              </div>
                              <div className="summary-item">
                                <Waves size={14} />
                                <span>Pools / Cabana</span>
                              </div>
                            </div>
                          </div>

                          <div className="layout-zone-card middle-right">
                            <div className="zone-card-header">
                              <Home className="zone-icon" size={20} />
                              <div className="zone-indicator middle-right-indicator"></div>
                              <h5>Home & Kitchen</h5>
                            </div>
                            <p className="zone-description">Appliances, kitchenware, home security & lighting</p>
                            <div className="zone-summary">
                              <div className="summary-item">
                                <Home size={14} />
                                <span>Aisles 100-108</span>
                              </div>
                            </div>
                            <button
                              className="expand-rows-button"
                              onClick={() => {
                                setViewModeBeforeAisle('map');
                                setIsMapFullscreen(false);
                                setAisleModalOpen('middle-right');
                              }}
                            >
                              <span>View Aisles 100-108</span>
                              <ChevronDown size={16} />
                            </button>
                          </div>

                          {/* FRONT SECTION */}
                          <div className="store-section-header store-section-front">FRONT OF STORE</div>
                          {/* FRONT ROW (Bottom) */}
                          <div className="layout-zone-card front-left">
                            <div className="zone-card-header">
                              <Pill className="zone-icon" size={20} />
                              <div className="zone-indicator front-left-indicator"></div>
                              <h5>Health & Wellness</h5>
                            </div>
                            <p className="zone-description">Alcohol, supplements, health products & personal care</p>
                            <div className="zone-summary">
                              <div className="summary-item">
                                <Wine size={14} />
                                <span>Aisles 300-308</span>
                              </div>
                            </div>
                            <button
                              className="expand-rows-button"
                              onClick={() => {
                                setViewModeBeforeAisle('map');
                                setIsMapFullscreen(false);
                                setAisleModalOpen('front-left');
                              }}
                            >
                              <span>View Aisles 300-308</span>
                              <ChevronDown size={16} />
                            </button>
                          </div>

                          <div className="layout-zone-card front-middle">
                            <div className="zone-card-header">
                              <Gamepad2 className="zone-icon" size={20} />
                              <div className="zone-indicator front-middle-indicator"></div>
                              <h5>Toys & Snacks</h5>
                            </div>
                            <p className="zone-description">Children's toys & packaged food items</p>
                            <div className="zone-summary">
                              <div className="summary-item">
                                <Gamepad2 size={14} />
                                <span>Toys</span>
                              </div>
                              <div className="summary-item">
                                <Box size={14} />
                                <span>Packaged Food</span>
                              </div>
                            </div>
                          </div>

                          <div className="layout-zone-card front-right">
                            <div className="zone-card-header">
                              <Laptop className="zone-icon" size={20} />
                              <div className="zone-indicator front-right-indicator"></div>
                              <h5>Electronics & Tech</h5>
                            </div>
                            <p className="zone-description">Technology, entertainment systems & jewellery</p>
                            <div className="zone-summary">
                              <div className="summary-item">
                                <Laptop size={14} />
                                <span>Technology</span>
                              </div>
                              <div className="summary-item">
                                <Tv size={14} />
                                <span>Home Entertainment</span>
                              </div>
                              <div className="summary-item">
                                <Headphones size={14} />
                                <span>Audio Visual</span>
                              </div>
                              <div className="summary-item">
                                <Laptop size={14} />
                                <span>Computer</span>
                              </div>
                              <div className="summary-item">
                                <Gem size={14} />
                                <span>Jewellery</span>
                              </div>
                            </div>
                          </div>

                          {/* Entrance/Exit/Checkout Cards */}
                          <div className="layout-zone-card checkout-card">
                            <div className="zone-card-header">
                              <CreditCard className="zone-icon" size={20} />
                              <h5>Checkout</h5>
                            </div>
                            <p className="zone-description">Front Left</p>
                          </div>

                          <div className="layout-zone-card exit-card">
                            <div className="zone-card-header">
                              <LogOut className="zone-icon" size={20} />
                              <h5>Exit</h5>
                            </div>
                            <p className="zone-description">Front Middle</p>
                          </div>

                          <div className="layout-zone-card entrance-card">
                            <div className="zone-card-header">
                              <LogIn className="zone-icon" size={20} />
                              <h5>Entrance</h5>
                            </div>
                            <p className="zone-description">Front Right</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Map Hint */}
                  <div className="map-fullscreen-hint">
                    <span>Pinch to zoom • Drag to pan</span>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>,
          document.body
        )}
      </div>
    </motion.div>
  );

  const renderSalesTab = () => {
    const salesCategories = [
      {
        name: 'In-Warehouse Savings',
        url: 'https://www.costco.com.au/warehouse-savings',
        description: 'Warehouse exclusive savings',
        color: 'hot-buys',
      },
      {
        name: 'Hot Buys',
        url: 'https://www.costco.com.au/c/hot-buys',
        description: 'Limited time hot deals',
        color: 'hot-buys',
      },
      {
        name: 'Last Chance',
        url: 'https://www.costco.com.au/c/last-chance',
        description: 'Final clearance items',
        color: 'last-chance',
      },
    ];

    return (
      <motion.div
        key="sales"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="tab-content"
      >
        <div className="costco-sales-section">
          <div className="sales-header">
            <h3>Current Sales</h3>
            <p className="sales-subtitle">Browse Costco latest deals and special offers</p>
          </div>

          <div className="sales-grid">
            {salesCategories.map((sale, index) => (
              <a
                key={index}
                href={sale.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`sales-card sales-card-${sale.color}`}
              >
                <div className={`sales-card-icon sales-card-icon-${sale.color}`}>
                  <Tag size={24} />
                </div>
                <div className="sales-card-content">
                  <h4>{sale.name}</h4>
                  <p className="sales-card-description">{sale.description}</p>
                </div>
                <div className={`sales-card-arrow sales-card-arrow-${sale.color}`}>
                  <ExternalLink size={18} />
                </div>
              </a>
            ))}
          </div>
        </div>
      </motion.div>
    );
  };

  const renderServicesTab = () => (
    <motion.div
      key="services"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="tab-content"
    >
      <div className="costco-services-section">
        <div className="services-overview">
          <h3>Available Services</h3>
          <div className="services-grid">
            <div className="service-card">
              <Eye size={24} />
              <h4>Optical</h4>
              <p>{location.optical_available ? 'Available' : 'Not Available'}</p>
            </div>
            <div className="service-card">
              <Wrench size={24} />
              <h4>Tyre Centre</h4>
              <p>{location.tyre_centre_available ? 'Available' : 'Not Available'}</p>
            </div>
            <div className="service-card">
              <ShoppingBag size={24} />
              <h4>Pharmacy</h4>
              <p>{location.pharmacy_available ? 'Available' : 'Not Available'}</p>
            </div>
            <div className="service-card">
              <Heart size={24} />
              <h4>Hearing Aids</h4>
              <p>{location.hearing_aids_available ? 'Available' : 'Not Available'}</p>
            </div>
          </div>
        </div>

        <div className="holiday-hours-section">
          <h3>Holiday Opening Hours</h3>
          <div className="holiday-grid">
            {Object.entries(location.holiday_opening_hours).map(([holiday, hours]) => (
              <div key={holiday} className="holiday-card">
                <h4>{holiday.replace('_', ' ')}</h4>
                <p>{hours}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="special-events-section">
          <h3>Special Events</h3>
          <div className="events-list">
            {location.special_events.map((event, index) => (
              <div key={index} className="event-item">
                <Calendar className="event-icon" size={18} />
                <span className="event-text">{event}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );

  const renderFuelTab = () => (
    <motion.div
      key="fuel"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="tab-content"
    >
      <div className="costco-fuel-section">
        <div className="fuel-station-info">
          <h3>Fuel Station</h3>
          <div className="fuel-details">
            <div className="fuel-item">
              <span className="fuel-label">Hours:</span>
              <span className="fuel-value">{location.fuel_station.hours}</span>
            </div>
            <div className="fuel-item">
              <span className="fuel-label">Notes:</span>
              <span className="fuel-value">{location.fuel_station.notes}</span>
            </div>
          </div>
        </div>

        <div className="fuel-location">
          <h3>Fuel Station Location</h3>
          <div className="coordinates-info">
            <div className="coordinate-item">
              <span className="coordinate-label">Latitude:</span>
              <span className="coordinate-value">{location.fuel_station.coordinates.latitude}</span>
            </div>
            <div className="coordinate-item">
              <span className="coordinate-label">Longitude:</span>
              <span className="coordinate-value">{location.fuel_station.coordinates.longitude}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="modal-overlay fullscreen"
      onClick={handleClose}
    >
      <motion.div
        ref={modalRef}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="location-modal fullscreen"
        onClick={(e) => e.stopPropagation()}
      >
        <div ref={headerRef} className="modal-header fullscreen">
          <div className="header-left">
            <h1 className="location-title">{location.name}</h1>
          </div>
          <div className="header-right">
            {/* Favorites button hidden for now */}
            {/* <button onClick={() => {}} className="icon-action-button" title="Save">
              <Heart size={18} />
            </button> */}
            <button onClick={handleShare} className="icon-action-button" title="Share">
              <Share2 size={18} />
            </button>
            <button onClick={handleClose} className="icon-action-button close" title="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div ref={tabsRef} className="modal-tabs">
          {tabs.map((tab) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                data-tab={tab.id}
                onClick={() => {
                  console.log('🔍 Tab clicked:', tab.id);
                  setActiveTab(tab.id);
                }}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              >
                <IconComponent size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <motion.div className="modal-content fullscreen">
          <AnimatePresence mode="wait">
            {activeTab === 'overview' && renderOverviewTab()}

            {activeTab === 'busyness' && (
              <BusynessTab
                convertedCurrentDateTime={convertedCurrentDateTime}
                getCurrentCapacity={getCurrentCapacity}
                getParkTime={getParkTime}
                getDayCapacities={getDayCapacities}
                capacities={location.capacities}
                selectedDay={selectedDay}
                setSelectedDay={setSelectedDay}
                selectedDateTimes={selectedDateTimes}
                name={location.name}
              />
            )}

            {activeTab === 'parking' && (
              <motion.div
                key="parking"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="tab-content"
              >
                <ParkingTab details={location} />
              </motion.div>
            )}

            {activeTab === 'food' && renderFoodTab()}

            {activeTab === 'store-layout' && renderStoreLayoutTab()}

            {activeTab === 'sales' && renderSalesTab()}

            {activeTab === 'services' && renderServicesTab()}

            {activeTab === 'fuel' && renderFuelTab()}

            {activeTab === 'traffic' && (
              <motion.div
                key="traffic"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="tab-content"
              >
                <TrafficTab
                  details={{
                    ...location,
                    details: {
                      latitude: location.latitude,
                      longitude: location.longitude,
                      name: location.name,
                    },
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Feedback Bubble - Hidden for now */}
        {/* <FeedbackBubble locationId={location.name} locationName={location.name} locationType="costco" /> */}
      </motion.div>

      {/* Aisle Detail Modal - Full Screen */}
      <AnimatePresence>
        {aisleModalOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="aisle-modal-overlay"
              onClick={() => {
                setAisleModalOpen(null);
                // Return to the view mode that was active when modal opened
                if (viewModeBeforeAisle === 'map') {
                  setIsMapFullscreen(true);
                  setViewMode('map');
                } else {
                  setIsMapFullscreen(false);
                  setViewMode(viewModeBeforeAisle);
                }
              }}
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="aisle-modal-fullscreen"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="aisle-modal-header-fullscreen">
                <div className="aisle-header-content">
                  <div>
                    <h2 className="aisle-modal-title">{getZoneTitle(aisleModalOpen)}</h2>
                    <p className="aisle-modal-subtitle-fullscreen">{getZoneAisleRange(aisleModalOpen)}</p>
                  </div>
                  <button
                    className="aisle-modal-close-fullscreen"
                    onClick={() => {
                      setAisleModalOpen(null);
                      // Return to the view mode that was active when modal opened
                      if (viewModeBeforeAisle === 'map') {
                        setIsMapFullscreen(true);
                        setViewMode('map');
                      } else {
                        setIsMapFullscreen(false);
                        setViewMode(viewModeBeforeAisle);
                      }
                    }}
                    aria-label="Close"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="aisle-modal-content-fullscreen">
                {/* Aisle Rows Section */}
                {getAisleData(aisleModalOpen)?.rows && getAisleData(aisleModalOpen)!.rows.length > 0 && (
                  <div className="aisle-section">
                    <div className="aisle-section-header">
                      <Layout size={24} />
                      <div>
                        <h3 className="aisle-section-title">Aisles</h3>
                        <p className="aisle-section-subtitle">Find items by aisle number</p>
                      </div>
                    </div>
                    <div className="aisle-rows-list">
                      {getAisleData(aisleModalOpen)?.rows.map((row, index) => (
                        <div key={index} className="aisle-row-card">
                          <div className="aisle-row-badge">
                            <span className="aisle-number">{row.number}</span>
                          </div>
                          <div className="aisle-row-content">
                            <p className="aisle-row-text">
                              {row.items
                                .split(', ')
                                .map((item) => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
                                .join(', ')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fridge Section */}
                {getAisleData(aisleModalOpen)?.fridges && getAisleData(aisleModalOpen)!.fridges!.length > 0 && (
                  <div className="aisle-section">
                    <div className="aisle-section-header">
                      <Refrigerator size={24} />
                      <div>
                        <h3 className="aisle-section-title">Refrigerated Sections</h3>
                        <p className="aisle-section-subtitle">Cold storage locations</p>
                      </div>
                    </div>
                    <div className="aisle-rows-list">
                      {getAisleData(aisleModalOpen)?.fridges?.map((fridge, index) => (
                        <div key={index} className="aisle-row-card fridge-card">
                          <div className="aisle-row-badge fridge-badge">
                            <Refrigerator size={16} />
                            <span className="aisle-number">{fridge.label}</span>
                          </div>
                          <div className="aisle-row-content">
                            <p className="aisle-row-text">
                              {fridge.items
                                .split(', ')
                                .map((item) => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
                                .join(', ')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CostcoModal;
